import { useCollaborateLoginMutation, useResendCodeMutation, useVerifyCodeMutation } from 'api/loginApi'
import { t } from 'i18next'
import { ChangeEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'sonner'
import LoginModalComponent from "ui/components/LoginModal"
import { bootstrapAction } from '../../../store/bootstrapSlice.ts'
import {
  getSelectedSiteCollaborateInfo,
  getSelectedSiteId,
} from '../../../store/siteInfoSlice.ts'
import { setShowForgotPassword, setShowLogin, setShowRegister, setSignupOption } from '../../../store/userSlice'

export const LoginModal = () => {
  const dispatch = useDispatch()
  const [login, { data: loginData, error: loginError, isLoading: loginLoading, isSuccess: loginSuccess }] = useCollaborateLoginMutation()
  const [resend] = useResendCodeMutation()
  const [verify, { error: verifyError, isLoading: verifyLoading, isSuccess: verifySuccess }] = useVerifyCodeMutation()
  const [error, setError] = useState<string | undefined>(undefined)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [screenshot, setScreenshot] = useState("")
  const [twoFactor, setTwoFactor] = useState(false)
  const siteId = useSelector(getSelectedSiteId)
  const siteInfo = useSelector(getSelectedSiteCollaborateInfo)

  type LoginError = { data: { status: boolean, error: boolean, message: string } }

  const activationCallback = new URL(window.location.toString()).searchParams.get('activation_callback');

  const handleCreateAccount = () => {
    dispatch(setShowRegister(true))
    dispatch(setShowForgotPassword(false))
    dispatch(setShowLogin(false))
    if (activationCallback) {
      dispatch(setSignupOption('regular'));
      return
    }
    dispatch(setSignupOption(''));
  }

  const handleForgotPassword = () => {
    dispatch(setShowForgotPassword(true))
    dispatch(setShowLogin(false))
    dispatch(setShowRegister(false))
  }

  const handleLogin = (e: SubmitEvent) => {
    e.preventDefault()
    login({ email, from_register: false, password, site_id: siteId ?? "" })
  }

  const handleLoginEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const handleLoginPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const handleResendCode = () => {
    resend()
  }

  const handleTwoFactor = (code: string) => {
    verify({ code, customFrom: null, customType: null, customUrl: null, planSearch: "" })
  }

  // useEffect(() => {
  //   const getScreenshot = async () => {
  //     const sc = new ScreenCapture()
  //     const data = await sc.capture({ useHtml2Canvas: true })
  //     setScreenshot(data)
  //   }
  //   getScreenshot()
  // }, []);

  useEffect(() => {
    if (loginError) {
      toast.error((loginError as LoginError).data.message)
      return
    }
    if (!loginSuccess || !loginData.result) return
    setError(undefined)
    if (loginData.result.two_factor_auth_enabled) {
      setTwoFactor(true)
    } else {
      dispatch(setShowForgotPassword(false))
      dispatch(setShowLogin(false))
      dispatch(setShowRegister(false))
      dispatch(bootstrapAction())
      toast.success(t('user_login.login_success', 'Logged in successfully!'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginError, loginSuccess]);

  useEffect(() => {
    if (verifyError) {
      setError((verifyError as LoginError).data.message)
      return
    }
    if (!verifySuccess) return
    dispatch(setShowForgotPassword(false))
    dispatch(setShowLogin(false))
    dispatch(setShowRegister(false))
    dispatch(bootstrapAction())
    toast.success(t('user_login.login_success', 'Logged in successfully!'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyError, verifySuccess])

  const whitelabelLogoFallback = 'https://wpfeedback-image.s3.us-east-2.amazonaws.com/settings/1629955997_Atarim-no-padding-original.png'

  return <LoginModalComponent
    collabMode={true}
    email={email}
    error={error}
    handleChangeLoginEmail={handleLoginEmailChange}
    handleChangeLoginPassword={handleLoginPasswordChange}
    handleConfirmVerification={handleTwoFactor}
    handleResendTwoFa={handleResendCode}
    handleCreateAccount={handleCreateAccount}
    handleForgotPassword={handleForgotPassword}
    handleGoogleLogin={() => { }}
    logoSrc={siteInfo?.result?.setting?.whitelabel.logo || whitelabelLogoFallback}
    imageGraphicItem=""
    itemDetails={siteInfo?.result}
    itemType=""
    is2FAEnabled={twoFactor}
    isLoginPending={loginLoading || verifyLoading}
    isMutating={false}
    loginUser={handleLogin}
    password={password}
    projectScreenshot={screenshot}
    projectUrl=""
    container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
  />
}